'use client'
/**
 * Call tones — synthesised, no audio assets
 * ─────────────────────────────────────────────────────────────────────────────
 * Generated with the Web Audio API rather than shipped as files, so there is
 * nothing to load, nothing to cache-bust, and no request that can fail while a
 * call is ringing.
 *
 *   ringback  outgoing — Indian exchange cadence: 400 Hz, 0.4s on, 0.2s off,
 *                        0.4s on, 2.0s silence
 *   ringtone  incoming — a two-note chime pair, repeating, deliberately more
 *                        musical than the ringback so the two are not confused
 *
 * Autoplay: browsers keep an AudioContext suspended until the page has been
 * interacted with. unlockTones() is wired to the first pointer/key event so the
 * context is already running by the time a call arrives unannounced. If it is
 * still blocked, isBlocked() reports it and the UI falls back to vibration and
 * the visual ring rather than failing silently.
 */

let ctx = null
let master = null
let stopCurrent = null
let unlocked = false

/*
 * A ring has to carry across a room, so it is deliberately louder than the end
 * tone. These are peak gains on a pure sine, not perceived loudness: the
 * incoming cadence sums two oscillators at 0.85 each, so 0.50 master puts that
 * pair at 0.85 combined — loud, and still short of clipping.
 */
const RING_VOLUME = 0.50
const END_VOLUME = 0.25

function context() {
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return null
  if (!ctx) {
    ctx = new Ctor()
    master = ctx.createGain()
    master.gain.value = RING_VOLUME
    master.connect(ctx.destination)
    routeToSpeaker(ctx)
  }
  return ctx
}

const setLevel = v => { if (master) master.gain.value = v }

// Output devices whose name says they are worn, and ones that say they are the
// speaker. Matched loosely because vendors name these inconsistently.
const WORN = /head(set|phone)|bluetooth|earpiece|hands[\s-]?free|airpod|buds|hearing/i
const SPEAKER = /speaker|internal|built[\s-]?in|loud/i

/**
 * Send the ring to the speaker rather than to whatever the system default is.
 *
 * The default is usually the headset — the same device the microphone belongs
 * to — so a ring played there reaches only someone already wearing it. An agent
 * who has stepped away from the desk hears nothing. This reproduces a desk
 * phone: ring out loud, talk into the handset, so call audio is left on the
 * default and only the ring is steered.
 *
 * Output labels are hidden until microphone permission has been granted, and an
 * agent grants it on answering, not before. So the first ring of a session may
 * still land on the default; from the second onward the speaker is chosen.
 * Without labels the devices cannot be told apart, so the default is left alone
 * rather than guessed at.
 */
let streamDest = null   // graph tap, when routing through a media element
let sinkEl = null       // the element whose output device can be chosen
let viaElement = false

/** Can the output device be chosen at all, and by which route? */
export function sinkSupport() {
  const c = context()

  if (!c) return 'none'

  // Element routing first: it is the ONLY path in Firefox, which has no
  // AudioContext.setSinkId. Chrome supports both.
  if (typeof c.createMediaStreamDestination === 'function' &&
      typeof HTMLMediaElement !== 'undefined' &&
      typeof HTMLMediaElement.prototype.setSinkId === 'function') return 'element'
  if (typeof c.setSinkId === 'function') return 'context'

  return 'none'
}

/**
 * Move the tone graph onto a media element bound to `deviceId`.
 *
 * The graph stays on ctx.destination until this succeeds, so a browser that
 * refuses the switch keeps the sound it already had rather than going quiet.
 * Only on success is the direct connection dropped — leaving both attached
 * would play the ring twice, once on each device.
 */
async function routeViaElement(deviceId) {
  const c = context()

  if (!streamDest) {
    streamDest = c.createMediaStreamDestination()
    sinkEl = new Audio()
    sinkEl.autoplay = true
    sinkEl.srcObject = streamDest.stream
  }

  await sinkEl.setSinkId(deviceId)
  await sinkEl.play()

  if (!viaElement) {
    master.connect(streamDest)
    try { master.disconnect(c.destination) } catch (_) {}
    viaElement = true
  }
}

export async function preferSpeaker() {
  const c = context()
  const how = sinkSupport()

  if (how === 'none') return null
  if (!navigator?.mediaDevices?.enumerateDevices) return null

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const outputs = devices.filter(d => d.kind === 'audiooutput' && d.label)

    if (!outputs.length) return null   // labels hidden — cannot choose

    const pick =
      outputs.find(d => SPEAKER.test(d.label) && !WORN.test(d.label)) ||
      outputs.find(d => !WORN.test(d.label))

    if (!pick) return null             // everything attached is worn

    if (how === 'element') await routeViaElement(pick.deviceId)
    else await c.setSinkId(pick.deviceId)

    return pick.label
  } catch (_) {
    return null
  }
}

/** Fall back to the system default output. */
function routeToSpeaker(c) {
  try {
    if (typeof c.setSinkId === 'function') c.setSinkId('').catch(() => {})
  } catch (_) { /* not supported */ }
}

/**
 * Hand the audio route back to the system.
 *
 * The ring is deliberately pinned to the loudspeaker, which on a phone puts the
 * whole audio session into loudspeaker/media mode. Cancelling the tone loop does
 * not undo that: sinkEl goes on playing a live — now silent — MediaStream, so
 * the route stays held for the rest of the call. The remote voice then comes out
 * of the loudspeaker, the microphone hears it, and that is the echo. The
 * browser's echo canceller is calibrated for the voice route it never got.
 *
 * Clearing srcObject alone is not enough: the destination's tracks stay live and
 * keep both the stream and the route alive.
 */
function releaseRoute() {
  const c = ctx
  if (!c) return

  if (viaElement) {
    try { master.connect(c.destination) } catch (_) {}
    try { master.disconnect(streamDest) } catch (_) {}
    viaElement = false
  }
  if (sinkEl) {
    try { sinkEl.pause(); sinkEl.srcObject = null } catch (_) {}
    sinkEl = null
  }
  if (streamDest) {
    try { streamDest.stream.getTracks().forEach(t => t.stop()) } catch (_) {}
    try { streamDest.disconnect() } catch (_) {}
    streamDest = null
  }
  routeToSpeaker(c)   // clears any context-level sink back to default
}

/**
 * Give up the audio session entirely, for when a call goes live.
 *
 * Suspending matters as well as unrouting: a running AudioContext is itself a
 * playback client, and on Android that alone can hold the output on the
 * loudspeaker even with nothing connected to it.
 */
export function releaseAudio() {
  stop()
  if (ctx && ctx.state === 'running') ctx.suspend().catch(() => {})
}

/** Resume the audio context. Safe to call repeatedly; must originate from a gesture. */
export function unlockTones() {
  const c = context()
  if (!c) return false
  if (c.state === 'suspended') c.resume().then(() => { unlocked = true }).catch(() => {})
  else unlocked = true
  return unlocked
}

export const isBlocked = () => {
  const c = context()
  // A suspend of our own (releaseAudio) is not the autoplay block this reports:
  // once the user has interacted, tones can always be resumed.
  return !c || (c.state === 'suspended' && !unlocked)
}

/**
 * Schedule one burst. Gain is ramped rather than switched so the tone does not
 * click on and off — square edges on a pure sine are audible and unpleasant.
 */
function burst(freq, startAt, duration, type = 'sine', peak = 1) {
  const c = context()
  if (!c) return
  const osc = c.createOscillator()
  const gain = c.createGain()
  osc.type = type
  osc.frequency.value = freq
  const attack = 0.015
  const release = Math.min(0.08, duration / 3)
  gain.gain.setValueAtTime(0, startAt)
  gain.gain.linearRampToValueAtTime(peak, startAt + attack)
  gain.gain.setValueAtTime(peak, startAt + duration - release)
  gain.gain.linearRampToValueAtTime(0, startAt + duration)
  osc.connect(gain)
  gain.connect(master)
  osc.start(startAt)
  osc.stop(startAt + duration + 0.02)
  return osc
}

/**
 * Run `pattern` on a loop until stopped. Each cycle is scheduled slightly ahead
 * of time, which keeps the cadence exact — setInterval alone drifts audibly.
 */
function loop(pattern, cycleSeconds) {
  const c = context()
  if (!c) return () => {}
  let cancelled = false
  let timer = null
  let nextAt = c.currentTime + 0.05

  const schedule = () => {
    if (cancelled) return
    pattern(nextAt)
    nextAt += cycleSeconds
    timer = setTimeout(schedule, Math.max(50, (nextAt - c.currentTime - 0.25) * 1000))
  }
  schedule()

  return () => {
    cancelled = true
    clearTimeout(timer)
  }
}

function play(builder, cycleSeconds) {
  stop()
  const c = context()
  if (!c) return
  if (c.state === 'suspended') c.resume().catch(() => {})
  stopCurrent = loop(builder, cycleSeconds)
}

/** Outgoing: the tone the caller hears while the far end rings. */
export function startRingback() {
  setLevel(RING_VOLUME)
  preferSpeaker()
  play(at => {
    burst(400, at, 0.4)
    burst(400, at + 0.6, 0.4)
  }, 3.4)
}

/**
 * Incoming: the classic desk-phone double ring — 400 Hz against 450 Hz, which
 * beat together into the familiar warble. Two bursts, then silence.
 *
 * The pair is at 0.85 each rather than full: two oscillators summing into the
 * same gain node would otherwise peak near 2.0 and clip audibly.
 */
export function startRingtone() {
  setLevel(RING_VOLUME)
  preferSpeaker()
  play(at => {
    for (let i = 0; i < 2; i++) {
      const s = at + i * 0.6
      burst(400, s, 0.4, 'sine', 0.85)
      burst(450, s, 0.4, 'sine', 0.85)
    }
  }, 3.4)
}

/** Short falling pair played once when a call ends. */
export function playEndTone() {
  stop()
  const c = context()
  if (!c) return
  if (c.state === 'suspended') c.resume().catch(() => {})
  setLevel(END_VOLUME)   // a goodbye, not an alert
  const at = c.currentTime + 0.02
  burst(480, at, 0.14, 'sine')
  burst(360, at + 0.16, 0.22, 'sine')
}

export { setLevel, RING_VOLUME, END_VOLUME }

export function stop() {
  if (stopCurrent) { stopCurrent(); stopCurrent = null }
  releaseRoute()
}

/** Vibration fallback for phones, and for when audio is still blocked. */
export function vibrateRing(on) {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return
  if (on) navigator.vibrate([600, 400, 600, 1400])
  else navigator.vibrate(0)
}

/**
 * Arm the audio context on the first interaction anywhere on the page. Without
 * this, the very first incoming call of a session would ring silently.
 */
export function armToneUnlock() {
  if (typeof window === 'undefined' || unlocked) return () => {}
  const handler = () => { if (unlockTones()) detach() }
  const events = ['pointerdown', 'keydown', 'touchstart']
  const detach = () => events.forEach(e => window.removeEventListener(e, handler))
  events.forEach(e => window.addEventListener(e, handler, { passive: true }))
  return detach
}
